import React, { useContext, useMemo, useRef } from "react";
import { useRouter } from "next/router";

import { MarketPlaceType } from "@vahak/core/dist/constants/market";
import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import { AppContext } from "@vahak/core/dist/app-context";
import { SelectOption } from "@vahak/core-ui/dist/components/Select";
import { scrollToElementById } from "@vahak/core-ui/dist/methods/dom";

import HomepageV2Animate from "../../../../homepage-v2/animate/HomepageV2Animate";
import { useTrackWebV2FieldOnce } from "../../../../homepage-v2/analytics/homepageV2WebAnalytics";
import {
    trackWebV2LocationField,
    trackWebV2LorryTypeSelected
} from "../../../../homepage-v2/analytics/trackLoadQuoteFormAnalytics";
import { IMarketplaceConfig, MARKETPLACE_ACTIONS, MarketplaceContext } from "../../../../../MarketplaceContextProvider";
import { IEventTrackerProp } from "../../../../../types/events";
import { MARKETPLACE_V3_LOADS_SECTION_ID } from "../marketplaceV3.constants";
import useMarketplaceV3Translation from "../useMarketplaceV3Translation";
import Styles from "./MarketplaceV3HeroSection.module.scss";
import MarketplaceLoadFilterCard from "../MarketplaceLoadFilterCard";
import { MarketplaceFilterFieldFocusHandle } from "../filter-fields/MarketplaceFilterStateSingleField";

function getHeroTitleLines(titleLines: unknown): string[] {
    if (Array.isArray(titleLines)) {
        return titleLines.filter((line): line is string => typeof line === "string");
    }

    return [];
}

type MarketplaceV3HeroSectionProps = IEventTrackerProp & {
    updateUrl: (config: IMarketplaceConfig) => void;
};

const MarketplaceV3HeroSection = ({ trackEvent, eventData, updateUrl }: MarketplaceV3HeroSectionProps) => {
    const { t } = useMarketplaceV3Translation();
    const router = useRouter();
    const titleLines = getHeroTitleLines(t("hero.titleLines", { returnObjects: true }));
    const { companyId, isPhoneVerified, setShouldPreventLayoutSwitch } = useContext(AppContext);
    const { marketplaceConfig, dispatchMarketplace, setShouldExecuteApi } = useContext(MarketplaceContext);
    const srcFieldRef = useRef<MarketplaceFilterFieldFocusHandle>(null!);

    const isLoggedIn = Boolean(companyId && isPhoneVerified);
    const marketplaceEventData = useMemo(
        () => ({ ...eventData, source_screen: WEB_V2_SOURCE_SCREENS.MARKETPLACE }),
        [eventData]
    );
    const { trackFieldOnce } = useTrackWebV2FieldOnce(trackEvent, marketplaceEventData);

    const hasActiveFilters =
        !!marketplaceConfig?.srcStateId ||
        !!marketplaceConfig?.destStateIds?.length ||
        !!marketplaceConfig?.lorryTypeIds?.length;

    const onSrcChange = (value?: SelectOption<number>) => {
        dispatchMarketplace({
            type: MARKETPLACE_ACTIONS.APPLY_FILTER,
            payload: {
                ...marketplaceConfig,
                srcStateId: value?.value,
                srcStateIdOption: value
            }
        });

        if (value) {
            trackWebV2LocationField(trackFieldOnce, {
                field: "pickup",
                sourceScreen: WEB_V2_SOURCE_SCREENS.MARKETPLACE,
                location: value.label
            });
        }
    };

    const onDestChange = (value?: SelectOption<number>[]) => {
        dispatchMarketplace({
            type: MARKETPLACE_ACTIONS.APPLY_FILTER,
            payload: {
                ...marketplaceConfig,
                destStateIds: value?.map((state) => state.value),
                destStateIdsOption: value || []
            }
        });

        const nextDest = value?.[value.length - 1];
        if (nextDest) {
            trackWebV2LocationField(trackFieldOnce, {
                field: "drop",
                sourceScreen: WEB_V2_SOURCE_SCREENS.MARKETPLACE,
                location: nextDest.label
            });
        }
    };

    const onLorryTypeChange = (lorryTypeIds: number[]) => {
        dispatchMarketplace({
            type: MARKETPLACE_ACTIONS.APPLY_FILTER,
            payload: {
                ...marketplaceConfig,
                lorryTypeIds
            }
        });

        const latestId = lorryTypeIds[lorryTypeIds.length - 1];
        if (latestId != null) {
            trackWebV2LorryTypeSelected(trackFieldOnce, {
                sourceScreen: WEB_V2_SOURCE_SCREENS.MARKETPLACE,
                lorryTypeId: latestId,
                dedupeSuffix: latestId
            });
        }
    };

    const onSearch = () => {
        if (!marketplaceConfig.srcStateIdOption) {
            srcFieldRef.current?.focus();
            return;
        }

        trackEvent?.(GA4EventNames.WEB_V2.market_search_clicked, marketplaceEventData);

        if (!isLoggedIn) {
            setShouldPreventLayoutSwitch?.(true);
            if (!router.asPath.includes("Login")) {
                router.push({ hash: "Login" }, undefined, { shallow: true });
            }
            return;
        }

        updateUrl(marketplaceConfig);
        requestAnimationFrame(() => scrollToElementById(MARKETPLACE_V3_LOADS_SECTION_ID, { focus: false }));
    };

    const onClearFilters = () => {
        trackEvent?.(GA4EventNames.MARKET_PLACE.filters_cleared, marketplaceEventData);
        dispatchMarketplace({ type: MARKETPLACE_ACTIONS.CLEAR_FILTER, payload: {} });

        const baseUrl = ROUTES.marketplace.getUrl(MarketPlaceType.LOAD);
        if (router.asPath.split("?")[0] !== baseUrl) {
            router.push(baseUrl, undefined, { shallow: true, scroll: false });
        }

        setShouldExecuteApi(true);
    };

    return (
        <section className={Styles.section}>
            <div className={Styles.container}>
                <HomepageV2Animate variant="fade-up">
                    <h1 className={Styles.title}>
                        {titleLines.map((line) => (
                            <span key={line} className={Styles.titleLine}>
                                {line}
                            </span>
                        ))}
                    </h1>
                </HomepageV2Animate>

                <HomepageV2Animate variant="fade-up" delay={80}>
                    <p className={Styles.description}>{t("hero.description")}</p>
                </HomepageV2Animate>

                <div className={Styles.searchWrap}>
                    <MarketplaceLoadFilterCard
                        eyebrow={t("search.eyebrow")}
                        fromPlaceholder={t("search.fromPlaceholder")}
                        toPlaceholder={t("search.toPlaceholder")}
                        lorryTypePlaceholder={t("search.lorryTypePlaceholder")}
                        searchLabel={t("search.cta")}
                        clearFiltersLabel={t("search.clearFilters")}
                        hasActiveFilters={hasActiveFilters}
                        srcState={marketplaceConfig.srcStateIdOption}
                        destStates={marketplaceConfig.destStateIdsOption || []}
                        lorryTypeIds={marketplaceConfig.lorryTypeIds || []}
                        srcFieldRef={srcFieldRef}
                        onSrcChange={onSrcChange}
                        onDestChange={onDestChange}
                        onLorryTypeChange={onLorryTypeChange}
                        onSearch={onSearch}
                        onClearFilters={onClearFilters}
                    />
                </div>
            </div>
        </section>
    );
};

export default MarketplaceV3HeroSection;
